/**
 * Helpers para consumir SSE desde el backend.
 *
 * - `streamPostSse`: para endpoints POST que responden con SSE (no se puede usar EventSource).
 * - `subscribeGetSse`: para endpoints GET con SSE — wrapper sobre EventSource.
 */
const BASE = import.meta.env.VITE_API_URL ?? '/api';

export interface SsePostEvent {
  type: string;
  data: unknown;
}

export async function streamPostSse(
  path: string,
  body: unknown,
  onEvent: (evt: SsePostEvent) => void,
  signal?: AbortSignal,
): Promise<void> {
  const res = await fetch(`${BASE}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'text/event-stream',
    },
    body: JSON.stringify(body),
    signal,
  });

  if (!res.ok || !res.body) {
    throw new Error(`SSE POST falló: ${res.status} ${res.statusText}`);
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });

    // Parsear eventos SSE separados por doble newline
    let idx: number;
    while ((idx = buffer.indexOf('\n\n')) !== -1) {
      const block = buffer.slice(0, idx);
      buffer = buffer.slice(idx + 2);
      const evt = parseSseBlock(block);
      if (evt) onEvent(evt);
    }
  }
}

function parseSseBlock(block: string): SsePostEvent | null {
  const lines = block.split('\n');
  let event = 'message';
  const dataParts: string[] = [];
  for (const line of lines) {
    if (line.startsWith('event:')) event = line.slice(6).trim();
    else if (line.startsWith('data:')) dataParts.push(line.slice(5).trim());
  }
  if (dataParts.length === 0) return null;
  const dataStr = dataParts.join('\n');
  try {
    return { type: event, data: JSON.parse(dataStr) };
  } catch {
    return { type: event, data: dataStr };
  }
}

// Tipos de eventos SSE que el backend emite con `event: <type>` desde Nest @Sse().
// EventSource.onmessage SOLO recibe eventos sin `event:` o con `event: message`,
// así que para los demás hay que hacer addEventListener explícito por tipo.
const KNOWN_SSE_EVENT_TYPES = [
  'job.completed',
  'job.failed',
  'message.created',
  'message.complete',
] as const;

export function subscribeGetSse(
  path: string,
  onEvent: (evt: SsePostEvent) => void,
): () => void {
  const url = `${BASE}${path}`;
  const es = new EventSource(url, { withCredentials: true });

  const dispatch = (type: string, raw: string) => {
    try {
      onEvent({ type, data: JSON.parse(raw) });
    } catch {
      onEvent({ type, data: raw });
    }
  };

  // Eventos sin tipo (default) o con `event: message`
  es.onmessage = (e) => dispatch('message', e.data);

  // Eventos con `event: <type>` — Nest @Sse() los emite cuando el Observable
  // devuelve `{ data, type }`. Registramos uno por tipo conocido.
  const namedHandlers: Array<[string, EventListener]> = KNOWN_SSE_EVENT_TYPES.map((t) => {
    const handler: EventListener = (e) => dispatch(t, (e as MessageEvent).data);
    es.addEventListener(t, handler);
    return [t, handler];
  });

  return () => {
    for (const [t, h] of namedHandlers) es.removeEventListener(t, h);
    es.close();
  };
}
